import React, { useEffect, useRef, useMemo, useState } from "react";
import DateRangePicker from "react-bootstrap-daterangepicker";
import ReactApexChart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { all_routes } from "../../../router/backendComponents/admin/all_routes";
import apiRequestCall from "../../../apiHelper/globalHelper";
import Select from "react-select";
import moment from "moment";

const fmtCompact = (v: number) =>
  new Intl.NumberFormat("en", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(Number(v) || 0);

const ellipsize = (s: unknown, n = 12) => {
  const str = String(s ?? "");
  return str.length > n ? str.slice(0, n - 1) + "…" : str;
};
interface KV {
  id: number;
  name: string;
}

const toOptions = (arr: KV[]) =>
  arr.map((x) => ({ value: x.id, label: x.name }));

const asArray = <T,>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);
const clamp = (v: number, min: number, max: number) =>
  Math.max(min, Math.min(max, v));

const QuotationSummary: React.FC = () => {
  const backendRoute = all_routes.backend;

  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);

  /* status donut */
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusChart, setStatusChart] = useState<{
    labels: string[];
    series: number[];
  }>({
    labels: ["open", "win", "lose", "close"],
    series: [0, 0, 0, 0],
  });

  const [formData, setFormData] = useState<any>({
    branch_id: "",
  });

  /* branch bar */
  const [branchLoading, setBranchLoading] = useState(false);
  const [branchCategories, setBranchCategories] = useState<string[]>([]);
  const [branchSeries, setBranchSeries] = useState<
    { name: string; data: number[] }[]
  >([]);
  const [branchYMax, setBranchYMax] = useState<number | undefined>(undefined);
  const [branchGrandTotal, setBranchGrandTotal] = useState<number>(0);
  const [branchList, setBranchList] = useState<KV[]>([]);

  /* owner radial (paged) */
  const [ownerLoading, setOwnerLoading] = useState(false);
  const [ownerCategories, setOwnerCategories] = useState<string[]>([]);
  const [ownerSeries, setOwnerSeries] = useState<number[]>([]);
  const [ownerTotal, setOwnerTotal] = useState<number>(0);
  const pickerRef = useRef<any>(null);
  /* owner paging */
  const [ownerPage, setOwnerPage] = useState<number>(0);
  const pageSize = 6;

  useEffect(
    () => setOwnerPage(0),
    [startDate, endDate, ownerCategories.length]
  );

  /* date range */
  const onDateRange = (start: any, end: any) => {
    setStartDate(moment(start).format("YYYY-MM-DD"));
    setEndDate(moment(end).format("YYYY-MM-DD"));
  };

  useEffect(() => {
    const end = moment();
    const start = moment().subtract(1, "month");

    if (pickerRef.current) {
      pickerRef.current.setStartDate(start);
      pickerRef.current.setEndDate(end);
    }

    setStartDate(start.format("YYYY-MM-DD"));
    setEndDate(end.format("YYYY-MM-DD"));
  }, []);

  /* ---------- APIs ---------- */
  const fetchStatus = async (startDate: string, endDate: string) => {
    setStatusLoading(true);
    try {
      const res = await apiRequestCall(
        "post",
        backendRoute.quotationStatusReport,
        {
          data: {
            start_date: startDate,
            end_date: endDate,
            branch_id: formData?.branch_id,
          },
        }
      );
      const payload = res?.data?.data ?? {};
      const labels = asArray<string>(payload.labels);
      const series = asArray<number | string>(payload.series).map(
        (n) => Number(n) || 0
      );
      setStatusChart({
        labels: labels.length ? labels : ["open", "win", "lose", "close"],
        series: series.length ? (series as number[]) : [0, 0, 0, 0],
      });
    } finally {
      setStatusLoading(false);
    }
  };

  const fetchBranch = async (startDate: string, endDate: string) => {
    setBranchLoading(true);
    try {
      const res = await apiRequestCall(
        "post",
        backendRoute.quotationBranchReport,
        {
          data: {
            start_date: startDate,
            end_date: endDate,
            branch_id: formData?.branch_id,
          },
        }
      );

      const payload = res?.data?.data ?? {};

      setBranchCategories(asArray<string>(payload.categories));

      const series = asArray<any>(payload.series).map((s) => ({
        name: s.name,
        data: asArray<number | string>(s.data).map((n) => Number(n) || 0),
      }));

      setBranchSeries(series);
      setBranchGrandTotal(Number(payload.grand_total) || 0);
    } finally {
      setBranchLoading(false);
    }
  };

  const fetchOwner = async (startDate: string, endDate: string) => {
    setOwnerLoading(true);
    try {
      const res = await apiRequestCall(
        "post",
        backendRoute.quotationOwnerReport,
        {
          data: {
            start_date: startDate,
            end_date: endDate,
            branch_id: formData?.branch_id,
          },
        }
      );
      const payload = res?.data?.data ?? {};
      const oc = asArray<string>(payload.categories).map((c) =>
        String(c ?? "")
      );
      const s0 = Array.isArray(payload.series)
        ? payload.series[0]
        : { data: [] };
      const vals = asArray<number | string>(s0?.data).map(
        (n) => Number(n) || 0
      );

      setOwnerCategories(oc as string[]);
      setOwnerSeries(vals as number[]);
      setOwnerTotal(
        typeof payload.grand_total === "number"
          ? payload.grand_total
          : (vals as number[]).reduce((a, b) => a + b, 0)
      );
    } finally {
      setOwnerLoading(false);
    }
  };

  type PrincipalSeries = { name: string; data: number[]; color?: string };
  type DealerSeries = { name: string; data: number[]; color?: string };

  const [pvdLoading, setPvdLoading] = useState(false);
  const [pvdMonths, setPvdMonths] = useState<string[]>([]);
  const [pvdPrincipals, setPvdPrincipals] = useState<PrincipalSeries[]>([]);
  const [pvdDealer, setPvdDealer] = useState<DealerSeries | null>(null);

  const fetchPrincipalDealer = async (startDate: string, endDate: string) => {
    setPvdLoading(true);
    try {
      const res = await apiRequestCall(
        "post",
        backendRoute.quotationPrincipalDealerReport,
        {
          data: {
            start_date: startDate,
            end_date: endDate,
            branch_id: formData?.branch_id,
          },
        }
      );

      const payload = res?.data?.data ?? res?.data ?? {};

      const months = asArray<string>(payload.months);
      const principals = asArray<any>(payload.principals).map((p) => ({
        name: String(p?.name ?? "Principal"),
        data: asArray<number | string>(p?.data).map((n) => Number(n) || 0),
        color: typeof p?.color === "string" ? p.color : undefined,
      })) as PrincipalSeries[];

      const dealerRaw = payload.dealer ?? null;
      const dealer: DealerSeries | null = dealerRaw
        ? {
            name: String(dealerRaw?.name ?? "Dealer"),
            data: asArray<number | string>(dealerRaw?.data).map(
              (n) => Number(n) || 0
            ),
            color:
              typeof dealerRaw?.color === "string"
                ? dealerRaw.color
                : undefined,
          }
        : null;

      setPvdMonths(months);
      setPvdPrincipals(principals);
      setPvdDealer(dealer);
    } finally {
      setPvdLoading(false);
    }
  };

  const pvdSeriesMixed = useMemo(() => {
    const bars = pvdPrincipals.map((p) => ({
      ...p,
      type: "bar" as const,
      stack: "authorized" as const,
    }));
    const line = pvdDealer ? [{ ...pvdDealer, type: "line" as const }] : [];
    return [...bars, ...line];
  }, [pvdPrincipals, pvdDealer]);

  const pvdColors = useMemo(
    () => pvdSeriesMixed.map((s: any) => s.color).filter(Boolean) as string[],
    [pvdSeriesMixed]
  );

  const pvdStrokeWidths = useMemo(() => {
    const barCount = pvdPrincipals.length;
    return Array(barCount)
      .fill(0)
      .concat(pvdDealer ? [3] : []);
  }, [pvdPrincipals.length, pvdDealer]);

  const pvdOptions: ApexOptions = useMemo(
    () => ({
      chart: {
        type: "line",
        height: 273,
        stacked: true,
        zoom: { enabled: true },
        toolbar: { show: true },
      },
      xaxis: {
        categories: pvdMonths,
        tickPlacement: "between",
        labels: { style: { colors: "#6B7280", fontSize: "11px" } },
        title: { text: "Month" },
      },
      yaxis: {
        labels: {
          style: { colors: "#6B7280", fontSize: "11px" },
          formatter: (v: number) => fmtCompact(v),
        },
        title: { text: "Amount" },
      },
      plotOptions: {
        bar: {
          columnWidth: "55%",
          borderRadius: 6,
          borderRadiusApplication: "around",
        },
      },
      dataLabels: { enabled: false },
      stroke: { width: pvdStrokeWidths, curve: "smooth" },
      markers: { size: 0, hover: { size: 5 } },
      legend: { position: "top", horizontalAlign: "left" },
      tooltip: {
        shared: true,
        y: { formatter: (v: number) => fmtCompact(Number(v)) },
      },
      colors: pvdColors.length ? pvdColors : undefined, // use API colors if provided
      grid: { strokeDashArray: 4 },
    }),
    [pvdMonths, pvdStrokeWidths, pvdColors]
  );

  /* boot/load */
  useEffect(() => {
    if (startDate && endDate) {
      handleSubmit(startDate, endDate);
    }
  }, [startDate, endDate, formData]);

  /* ---------- STATUS (donut) ---------- */
  const donutOptions: ApexOptions = useMemo(
    () => ({
      chart: { type: "donut", height: 240, toolbar: { show: false } },
      colors: ["#1ecbe2", "#5cb85c", "#fda700", "#fc0027"],
      labels: statusChart.labels,
      dataLabels: { enabled: false },
      legend: {
        show: true,
        position: "bottom",
        fontSize: "12px",
        markers: { size: 10 },
      },
      plotOptions: { pie: { donut: { size: "55%", labels: { show: false } } } },
      stroke: { lineCap: "round", show: true, width: 0, colors: ["#fff"] },
      tooltip: { y: { formatter: (v) => fmtCompact(Number(v)) } },
      responsive: [
        {
          breakpoint: 576,
          options: {
            chart: { height: 220 },
            legend: { fontSize: "11px", markers: { size: 8 } },
          },
        },
      ],
    }),
    [statusChart.labels]
  );

  /* ---------- BRANCH (vertical bar) ---------- */
  const branchOptions: ApexOptions = {
    chart: {
      type: "bar",
      stacked: true,
      height: 280,
    },
    tooltip: {
      shared: true,
      intersect: false, // ✅ FIX
      y: {
        formatter: (v) => fmtCompact(Number(v)),
      },
    },
    xaxis: {
      categories: branchCategories,
    },
    colors: ["#1ecbe2", "#5cb85c", "#fda700", "#fc0027"],
    legend: { position: "top" },
    dataLabels: { enabled: false },
  };

  /* ---------- OWNER (radial with paging) ---------- */
  const oc = asArray<string>(ownerCategories);
  const os = asArray<number>(ownerSeries);
  const ownerPages = Math.max(1, Math.ceil(oc.length / pageSize));
  const safePage = clamp(ownerPage, 0, ownerPages - 1);

  useEffect(() => {
    if (ownerPage !== safePage) setOwnerPage(safePage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safePage]);

  const ownerStart = safePage * pageSize;
  const ownerEnd = clamp(ownerStart + pageSize, 0, oc.length);
  const ownerPageLabels = useMemo(
    () => oc.slice(ownerStart, ownerEnd),
    [oc, ownerStart, ownerEnd]
  );
  const ownerPageSeries = useMemo(
    () => os.slice(ownerStart, ownerEnd),
    [os, ownerStart, ownerEnd]
  );

  const ownerRadialOptions: ApexOptions = useMemo(() => {
    const pageTotal = ownerPageSeries.reduce((a, b) => a + (Number(b) || 0), 0);
    const labels = ownerPageLabels.length
      ? ownerPageLabels
      : ownerPageSeries.map((_, i) => `Owner ${i + 1}`);

    return {
      chart: { type: "radialBar", height: 400, toolbar: { show: false } },
      labels: labels.map((n) => ellipsize(n, 14)),
      colors: [
        "#1B84FF",
        "#5CB85C",
        "#FF6F28",
        "#8E44AD",
        "#FFC107",
        "#16A085",
        "#2C3E50",
        "#2ECC71",
      ],
      plotOptions: {
        radialBar: {
          hollow: { size: "30%" },
          track: { strokeWidth: "95%" },
          dataLabels: {
            name: { fontSize: "12px" },
            value: {
              fontSize: "14px",
              formatter: (v) => fmtCompact(Number(v)),
            },
            total: {
              show: true,
              label: "Page Total",
              formatter: () => fmtCompact(pageTotal),
            },
          },
        },
      },
      legend: {
        show: true,
        position: "bottom",
        fontSize: "12px",
        markers: { size: 9 },
      },
      tooltip: {
        y: { formatter: (v) => fmtCompact(Number(v)) },
        custom: ({ seriesIndex }: any) => {
          const fullName = String(labels[seriesIndex] ?? "");
          const value = Number(ownerPageSeries[seriesIndex] ?? 0);
          return `<div class="apex-tooltip" style="padding:6px 8px;font-size:12px">
            <div><strong>${fullName}</strong></div>
            <div>${fmtCompact(value)}</div>
          </div>`;
        },
      },
      responsive: [
        {
          breakpoint: 576,
          options: { chart: { height: 360 }, legend: { markers: { size: 8 } } },
        },
      ],
    };
  }, [ownerPageLabels, ownerPageSeries]);

  const handleApply = (picker: any) => {
    const start = picker.startDate.format("YYYY-MM-DD");
    const end = picker.endDate.format("YYYY-MM-DD");

    // update state for UI consistency
    setStartDate(start);
    setEndDate(end);

    // call submit with real values, not from state
    handleSubmit(start, end);
  };

  const handleSubmit = async (start: string, end: string) => {
    if (submitting) return;

    setSubmitting(true);

    await Promise.all([
      fetchStatus(start, end),
      fetchBranch(start, end),
      fetchOwner(start, end),
      fetchPrincipalDealer(start, end),
    ]);

    setSubmitting(false);
  };

  const getLookup = async (endpoint: string) => {
    try {
      const r = await apiRequestCall("post", endpoint);
      const raw = r?.data?.data;
      if (raw && typeof raw === "object") {
        return Object.entries(raw).map(([id, name]) => ({
          id: +id,
          name: String(name),
        }));
      }
    } catch (err) {
      console.error(`Failed to fetch ${endpoint}`, err);
    }
    return [] as KV[];
  };

  const loadLookups = async () => {
    const [branches] = await Promise.all([getLookup(backendRoute.getBranchDD)]);
    setBranchList(branches);
  };

  useEffect(() => {
    loadLookups();
  }, []);

  /* ---------- render ---------- */
  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Header */}
        <div className="row">
          <div className="col-md-2">
            <div className="page-header d-flex align-items-center">
              <h3 className="page-title">Quotation Summary</h3>
            </div>
          </div>
          <div className="col-md-2">
            <div className="page-header d-flex align-items-center">
              <div className="d-flex">
                <div className="daterange-picker d-flex">
                  <div className="form-sort me-2">
                    <i className="ti ti-calendar" />
                    <DateRangePicker
                      ref={pickerRef}
                      onCallback={onDateRange}
                      onApply={handleSubmit}
                      onEvent={(event, picker) => {
                        if (event.type === "apply") {
                          handleApply(picker);
                        }
                      }}
                    >
                      <input
                        className="form-control bookingrange"
                        type="text"
                      />
                    </DateRangePicker>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-2">
            <div className="mb-2">
              <Select
                name="branch_id"
                classNamePrefix="react-select"
                className="tagging"
                options={toOptions(branchList)}
                value={
                  toOptions(branchList).find(
                    (o) => o.value === formData.branch_id
                  ) || null
                }
                onChange={(opt) => {
                  setFormData((prev: any) => ({
                    ...prev,
                    branch_id: opt?.value || null,
                  }));
                }}
                isClearable
                isDisabled={submitting}
              />
            </div>
          </div>
        </div>

        {/* Row 1: Status + Branch */}
        <div className="row">
          {/* Status (donut) */}
          <div className="col-lg-6 d-flex" style={{ marginBottom: "-35px" }}>
            <div className="card flex-fill">
              <div className="card-header pb-2">
                <h5 className="mb-0">Status Wise</h5>
              </div>
              <div className="card-body">
                {statusLoading ? (
                  <p>Loading…</p>
                ) : (
                  <ReactApexChart
                    options={donutOptions}
                    series={statusChart.series}
                    type="donut"
                    height={240}
                  />
                )}
              </div>
            </div>
          </div>

          <div className="col-lg-6 d-flex" style={{ marginBottom: "-35px" }}>
            <div className="card flex-fill">
              <div className="card-header pb-2">
                <h5 className="mb-0">Branch Wise</h5>
              </div>

              <div className="card-body">
                {branchLoading ? (
                  <p>Loading…</p>
                ) : branchSeries.length === 0 ? (
                  <p className="text-muted">No data</p>
                ) : (
                  <ReactApexChart
                    options={branchOptions}
                    series={branchSeries}
                    type="bar"
                    height={280}
                  />
                )}

                <div className="mt-2 text-end">
                  <small>Total: {fmtCompact(branchGrandTotal)}</small>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Row 2: Owner Radial (paged) + Principal vs Dealer */}
        <div className="row mt-4">
          <div className="col-lg-6 d-flex" style={{ marginBottom: "-35px" }}>
            <div className="card flex-fill">
              <div className="card-header pb-2 d-flex align-items-center justify-content-between">
                <h5 className="mb-0">Owner Wise</h5>
                {/* Pager */}
                {/* <div className="d-flex align-items-center gap-2">
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => setOwnerPage((p) => Math.max(0, p - 1))}
                    disabled={ownerPage <= 0}
                  >
                    ‹ Prev
                  </button>
                  <span style={{ fontSize: 12 }}>
                    Page{" "}
                    {Math.min(
                      ownerPage + 1,
                      Math.max(1, Math.ceil(ownerCategories.length / pageSize))
                    )}{" "}
                    of{" "}
                    {Math.max(1, Math.ceil(ownerCategories.length / pageSize))}
                  </span>
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() =>
                      setOwnerPage((p) =>
                        Math.min(
                          Math.max(
                            1,
                            Math.ceil(ownerCategories.length / pageSize)
                          ) - 1,
                          p + 1
                        )
                      )
                    }
                    disabled={
                      ownerPage >=
                      Math.max(
                        1,
                        Math.ceil(ownerCategories.length / pageSize)
                      ) -
                        1
                    }
                  >
                    Next ›
                  </button>
                </div> */}
              </div>

              <div className="card-body">
                {ownerLoading ? (
                  <p>Loading…</p>
                ) : ownerPageSeries.length === 0 ? (
                  <p className="text-muted mb-0">No data</p>
                ) : (
                  <ReactApexChart
                    options={ownerRadialOptions}
                    series={ownerPageSeries}
                    type="radialBar"
                    height={400}
                  />
                )}
                <div className="mt-2 text-end">
                  <small>Total: {fmtCompact(ownerTotal)}</small>
                </div>
              </div>
            </div>
          </div>

          {/* Principal vs Dealer (stacked bars + line) */}
          <div className="col-md-6 d-flex" style={{ marginBottom: "-35px" }}>
            <div className="card w-100">
              <div className="card-header border-0 pb-0">
                <div className="d-flex align-items-center justify-content-between flex-wrap row-gap-3">
                  <h4>
                    <i className="ti ti-grip-vertical me-1" />
                    Principal VS Dealer
                  </h4>
                </div>
              </div>
              <div className="card-body">
                {pvdLoading ? (
                  <p>Loading…</p>
                ) : pvdSeriesMixed.length === 0 ? (
                  <p className="text-muted mb-0">No data</p>
                ) : (
                  <ReactApexChart
                    options={pvdOptions}
                    series={pvdSeriesMixed as any}
                    type="line"
                    height={273}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuotationSummary;
